import { NotifyHelper } from 'classes/helpers/notify.helper';
import { IManageListDialogProps } from 'components/common/pitch-lists/manage-list';
import { t } from 'i18next';
import {
  DEFAULT_SIDEBAR_FILTERS,
  ISidebarFilters,
} from 'interfaces/pitch-lists/i-sidebar-filters';
import {
  DEFAULT_TOOLBAR_FILTERS,
  IToolbarFilters,
} from 'interfaces/pitch-lists/i-toolbar-filters';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { READ_ONLY_LIST_TYPES } from 'lib_ts/enums/pitches.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import {
  IPitchList,
  IPitchListPutManyRequest,
  IPitchListSummary,
  IRenameFolderRequest,
} from 'lib_ts/interfaces/pitches';
import { clone } from 'lodash';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import { create } from 'zustand';

const STORE_NAME = 'useListLibraryStore';

interface SidebarState {
  // open/close state for every folder across all roots
  sidebarOpenPaths: DictOf<boolean>;
  sidebarFilters: ISidebarFilters;
}

interface ToolbarState {
  toolbarFilters: IToolbarFilters;
}

interface DialogState {
  dialogManage:
    | {
        key: number;
        dialog: IManageListDialogProps;
      }
    | undefined;
}

interface ListsState {
  recentListIDs: string[];
  rawLists: IPitchList[];
  lists: IPitchList[];
  activeList: IPitchList | undefined;
  listSummaryDict: DictOf<IPitchListSummary>;
}

type State = SidebarState &
  ToolbarState &
  DialogState &
  ListsState & {
    loading: boolean;
  };

type Actions = {
  readonly isActiveListReadOnly: () => boolean;

  readonly refreshLists: (notify: boolean) => Promise<void>;

  readonly deleteLists: (ids: string[]) => Promise<boolean>;

  readonly renameFolder: (
    payload: IRenameFolderRequest,
    successMsg?: string
  ) => Promise<boolean>;

  readonly updateLists: (
    payload: IPitchListPutManyRequest,
    successMsg?: string
  ) => Promise<boolean>;
};

type ListLibraryStore = State & Actions;

const useListLibraryStore = create<ListLibraryStore>((set, get) => ({
  // MARK: store state
  loading: false,

  // MARK: lists state
  recentListIDs: [],
  rawLists: [],
  lists: [],
  activeList: undefined,
  listSummaryDict: {},

  // MARK: dialog state
  dialogManage: undefined,

  // MARK: sidebar state
  sidebarOpenPaths: {},
  sidebarFilters: clone(DEFAULT_SIDEBAR_FILTERS),

  // MARK: toolbar state
  toolbarFilters: clone(DEFAULT_TOOLBAR_FILTERS),

  // MARK: store actions
  isActiveListReadOnly: () => {
    // only super-admins can edit reference lists
    const { activeList } = get();

    if (!activeList) {
      return false;
    }

    const { userRoleFeatureFlags } = useAuthStore.getState();

    if (userRoleFeatureFlags.isSuperAdmin) {
      return false;
    }

    if (!activeList.reference_type) {
      return false;
    }

    return READ_ONLY_LIST_TYPES.includes(activeList.reference_type);
  },

  refreshLists: async (notify) => {
    try {
      if (notify) {
        NotifyHelper.success({
          message_md: t('common.refreshing-pitch-lists'),
          delay_ms: 3000,
        });
      }

      set({ loading: true });

      const { current, userRoleFeatureFlags } = useAuthStore.getState();

      const result = await PitchListsService.getInstance()
        .getVisible(userRoleFeatureFlags)
        .finally(() => set({ loading: false }));

      if (!result) {
        set({ rawLists: [] });
        return;
      }

      const filtered = result.filter((l) => {
        if (l.reference_type) {
          return true;
        }

        switch (l._parent_def) {
          case PitchListOwner.Team: {
            return current.team_lists;
          }

          case PitchListOwner.Machine: {
            return current.machine_lists;
          }

          /** always allow personal lists */
          case PitchListOwner.User: {
            return true;
          }

          /** suppress any malformed entries */
          default: {
            return false;
          }
        }
      });

      set({ rawLists: filtered });
    } catch (e) {
      console.error(STORE_NAME, e);
    }
  },

  renameFolder: async (payload, successMsg) => {
    try {
      set({ loading: true });

      const result = await PitchListsService.getInstance()
        .renameFolder(payload)
        .finally(() => set({ loading: false }));

      if (!result.success) {
        throw new Error(result.error);
      }

      NotifyHelper.success({
        message_md: successMsg ?? 'Folder renamed!',
      });

      /** triggers reload of rawLists => lists */
      await get().refreshLists(false);

      return true;
    } catch (e) {
      console.error(STORE_NAME, e);

      NotifyHelper.error({
        message_md:
          e instanceof Error
            ? e.message
            : `There was an error renaming your folder. ${ERROR_MSGS.CONTACT_SUPPORT}`,
      });

      return false;
    }
  },

  deleteLists: async (ids) => {
    try {
      set({ loading: true });

      const result = await PitchListsService.getInstance()
        .deleteMany(ids)
        .finally(() => set({ loading: false }));

      if (!result.success) {
        throw new Error(result.error);
      }

      NotifyHelper.success({
        message_md: `Pitch ${ids.length === 1 ? 'list' : 'lists'} deleted!`,
      });

      /** remove deleted lists from session */
      set((prev) => ({
        ...prev,
        rawLists: prev.rawLists.filter((l) => !ids.includes(l._id)),
      }));

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md:
          e instanceof Error ? e.message : t('common.request-failed-msg'),
      });

      return false;
    }
  },

  updateLists: async (payload, successMsg) => {
    try {
      set({ loading: true });

      const result = await PitchListsService.getInstance()
        .updateMany(payload)
        .finally(() => set({ loading: false }));

      if (!result.success) {
        throw new Error(result.error);
      }

      NotifyHelper.success({
        message_md: successMsg ?? 'Pitch lists updated!',
      });

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md:
          e instanceof Error
            ? e.message
            : `There was an error updating your pitch lists. ${ERROR_MSGS.CONTACT_SUPPORT}`,
      });

      return false;
    }
  },
}));

export default useListLibraryStore;
